<template>
  <div class="business-details">
    <h1>{{ 'business-details.header' | globalize }}</h1>
    <a-divider />
    <template v-if="isLoaded && !isFailed">
      <h2>{{ 'business-details.common-company-info' | globalize }}</h2>
      <template v-if="detailsOnly">
        <details-reader
          :copyable="true"
          :details="details"
        />
      </template>
      <template v-else>
        <details-reader
          :copyable="true"
          :details="companyInfo"
        />
        <ul
          class="app-key-value__list"
          v-if="roles.length > 0"
        >
          <li class="app-key-value__row">
            <span class="app-key-value__key">
              {{ 'business-details.lbl-role' | globalize }}
            </span>
            <roles
              :disabled="isPending || changeAddressRoleDisabled"
              :roles="roles"
              :role="role"
              :on-click="onRoleChange"
            />
          </li>
        </ul>
        <a-divider />
        <permission-wrapper
          skip-alert
          page-name="user"
          resource-name="address"
          action-name="read_foreign">
          <eth-addresses :addresses="addresses" />
        </permission-wrapper>
        <a-divider />
        <a-collapse expand-icon-position="right">
          <a-collapse-panel
            key="verification-details-company-info.docs-info"
            :header="'verification-details-company.title-docs' | globalize">
            <veriication-details-company-docs :company-docs="companyDocsAsFlatList" />
          </a-collapse-panel>

          <a-collapse-panel
            key="verification-details-company-info.beneficiaries"
            :header="'business-details.beneficiaries' | globalize">
            <company-beneficiaries
              :data-source="beneficiaries"
            />
          </a-collapse-panel>
        </a-collapse>
      </template>
    </template>
    <template v-else>
      <div class="app-list__li-like">
        <template v-if="isLoaded && !details">
          <p>
            {{ "business-details.nothing-here-yet" | globalize }}
          </p>
        </template>

        <template v-else-if="isFailed">
          <p>
            {{ "business-details.default-error-message" | globalize }}
          </p>
        </template>

        <template v-else>
          <p>
            {{ "business-details.loading-message" | globalize }}
          </p>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import ACollapse from 'ant-design-vue/lib/collapse/Collapse'
import ACollapsePanel from 'ant-design-vue/lib/collapse/CollapsePanel'
import ADivider from 'ant-design-vue/lib/divider'
import { api } from '@/api'
import DetailsReader from '@/vue/common/DetailsReader/DetailsReader'
import CompanyBeneficiaries from '@/vue/common/company-beneficiaries/beneficiaries-list'
import { ErrorHandler } from '@/js/helpers/error-handler'
import VerificationDetailsCompanyDocs from '../verifications/components/company-info/VeriicationDetailsCompanyDocs'
import { formatDate } from '@/js/utils/date.util'
import { explorerUrl, imageUrlToSumsubDocs } from '@/js/helpers/urls'
import { TYPES_AS_OBJECT } from '../../../js/records/entities/eth-address'
import EthAddresses from '../../common/eth-addresses'
import Roles from '../../common/roles'
import PermissionMixin from '@/vue/mixins/permission.mixin'
import PermissionWrapper from '@/vue/common/permission-wrapper'

export default {
  name: 'business-details',
  components: {
    DetailsReader,
    'veriication-details-company-docs': VerificationDetailsCompanyDocs,
    'a-collapse': ACollapse,
    'a-collapse-panel': ACollapsePanel,
    'a-divider': ADivider,
    CompanyBeneficiaries,
    EthAddresses,
    Roles,
    PermissionWrapper,
  },
  mixins: [ PermissionMixin ],
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  data () {
    return {
      isLoaded: false,
      isFailed: false,
      isPending: false,
      detailsOnly: false,
      details: {},
      companyInfo: {},
      companyDocs: [],
      beneficiaries: [],
      addresses: [],
      roles: [],
      role: '',
    }
  },
  computed: {
    companyDocsAsFlatList () {
      const { inspectionId } = this.companyInfo

      const flatList = []

      this.companyDocs.forEach(doc => {
        doc.images && doc.images.forEach(image => {
          image.url = imageUrlToSumsubDocs(inspectionId, image.imageId)
          const row = {
            ...image,
            group: doc.id,
          }

          flatList.push(row)
        })
      })

      return flatList
    },
    changeAddressRoleDisabled () {
      const result = this.isForbidden('user', 'address', 'update_foreign')
      return result
    },
    readAddressRoleDisabled () {
      const result = this.isForbidden('user', 'address', 'read_foreign')
      return result
    },
  },
  async created () {
    await this.initDetails()
  },
  methods: {
    async initDetails () {
      await this.getDetails()
      await this.getUserRoles()

      this.detailsOnly = (this.details.applicantId === '')
      if (this.detailsOnly) {
        delete this.details.results
        return
      }
      await this.getCompanyInfo()
      await this.getSumsubCompanyDocs()
      await this.getSumsubBeneficiaries()
    },

    async getDetails () {
      this.isLoaded = false
      this.isPending = true
      this.isFailed = false
      try {
        let { data } = await api.getWithSignature(`/users/businesses?filter[id]=${this.id}`)
        this.details = data[0]
        if (!this.details.address) {
          this.addresses = []
          return
        }

        this.details.cpkAddress.forEach(address => {
          const addr = {
            address: address.address,
            type: TYPES_AS_OBJECT.main,
            chainName: address.chainName,
            chainId: address.chainId,
            url: explorerUrl('address', address.address, address.explorerUrl),
          }
          this.addresses.push(addr)
          const proxy = {
            address: address.cpkAddress,
            type: TYPES_AS_OBJECT.proxy,
            chainId: address.chainId,
            chainName: address.chainName,
            url: explorerUrl('address', address.cpkAddress, address.explorerUrl),
          }
          this.addresses.push(proxy)
        })
      } catch (e) {
        console.error('err.getDetails', e)
        this.isFailed = true
        this.errorMessage = 'user-details.not-found-error-message'
        ErrorHandler.processWithoutFeedback(e)
      } finally {
        this.isLoaded = true
        this.isPending = false
      }
    },

    async getCompanyInfo () {
      const { applicantId } = this.details

      this.isLoaded = false
      this.isPending = true
      this.isFailed = false
      try {
        const companyInfo = await api.getWithSignature(
          `/sumsub/applicant/${applicantId}/company`
        )
        delete companyInfo.data.beneficiaries

        this.companyInfo = companyInfo.data
        this.companyInfo.status = this.details.status
        this.companyInfo.createdAt = formatDate(this.details.createdAt)
      } catch (e) {
        console.error('err.getCompanyInfo', e)
        this.isFailed = true
        ErrorHandler.processWithoutFeedback(e)
      } finally {
        this.isPending = false
        this.isLoaded = true
      }
    },
    async getSumsubBeneficiaries () {
      const { beneficiaries } = this.details.results.info.companyInfo
      this.isLoaded = false
      this.isPending = true
      this.isFailed = false
      try {
        const promises = []
        const docsPromises = []

        beneficiaries.forEach(row => {
          const promise = api.getWithSignature(
            `/sumsub/applicant/${row.applicantId}/individual`
          )
          promises.push(promise)

          const docPromise = api.getWithSignature(
            `/sumsub/applicant/${row.applicantId}/individual/docs`
          )
          docsPromises.push(docPromise)
        })
        const beneficiaryResponses = await Promise.all(promises)

        const docsResponses = await Promise.all(docsPromises)
        beneficiaries.forEach((row, index) => {
          const { inspectionId } = beneficiaryResponses[index].data

          beneficiaries[index] = {
            ...beneficiaryResponses[index].data,
            ...row,
            docs: this.fillDocsUrl(docsResponses[index].data, inspectionId),
          }
        })
        this.beneficiaries = beneficiaries
      } catch (e) {
        console.error('err.getSumsubBeneficiaries', e)
        this.isFailed = true
        ErrorHandler.processWithoutFeedback(e)
      } finally {
        this.isLoaded = true
        this.isPending = false
      }
    },

    fillDocsUrl (docs, inspectionId) {
      if (!docs || !docs.length) {
        return []
      }

      return docs.map(doc => {
        doc.url = imageUrlToSumsubDocs(inspectionId, doc.imageId[0])

        return doc
      })
    },

    async getSumsubCompanyDocs () {
      const { applicantId } = this.details

      this.isLoaded = false
      this.isPending = true
      this.isFailed = false
      try {
        const companyDocs = await api.getWithSignature(
          `/sumsub/applicant/${applicantId}/company/docs`
        )

        this.companyDocs = companyDocs.data
      } catch (e) {
        console.error('err.getSumsubCompanyDocs', e)
        this.isFailed = true
        ErrorHandler.processWithoutFeedback(e)
      } finally {
        this.isLoaded = true
        this.isPending = false
      }
    },

    async getUserRoles () {
      this.isLoaded = false
      this.isPending = true
      this.isFailed = false
      try {
        const { data } = await api.getWithSignature('/roles')

        this.roles = data.sort((a, b) => {
          if (a.id < b.id) {
            return -1
          }
          if (a.id > b.id) {
            return 1
          }
          return 0
        })
        this.role = this.roles.find(r => r.id === this.details.role)
      } catch (e) {
        console.error('err.getUserRoles', e)
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.isLoaded = true
        this.isPending = false
      }
    },

    async onRoleChange (role) {
      this.isPending = true
      this.isFailed = false

      this.role = role
      let body = {
        data: {
          type: 'address_role',
          attributes: {
            role: this.role.id,
          },
        },
      }

      try {
        const bodyAsString = JSON.stringify(body)
        await api.patchWithSignature(`/addresses/${this.details.address}/role`, bodyAsString)
      } catch (e) {
        console.error('err.onRoleChange', e)
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.isPending = false
      }
    },
  },
}

</script>

<style lang="scss">
  @import '~ant-design-vue/lib/collapse/style/index.css';
  @import '~ant-design-vue/lib/divider/style/index.css';
  @import '~ant-design-vue/lib/tag/style/index.css';

</style>
